/**
 * ConfirmSummary — /valuation/confirm
 *
 * The confirm-before-compute gate (Task 6 of the conversational-intake plan): an LLM
 * extracted these numbers during the chat interview, and the owner must see and be able
 * to correct every one of them before anything is computed. This is the safety property
 * the whole conversational-intake design rests on.
 *
 * Flow:
 *   - On mount: read intakeId from router state (ChatIntake navigates here with
 *     `{ intakeId }`), falling back to sessionStorage['intake_id'] (page refresh).
 *     Neither present -> friendly start-over state, no API calls.
 *   - Call completeIntake(intakeId) -> { mvaInput }. Render it grouped exactly like the
 *     wizard's own review step (Business basics / Earnings history / Balance sheet /
 *     Business strength), reusing LiteWizard's field styling and RiskQuestionStep for the
 *     26 ratings so the two review surfaces look and behave the same.
 *   - Every scalar field is editable inline; edits are local state only (a deep-edited
 *     copy of mvaInput) — nothing is sent anywhere until the owner clicks confirm.
 *   - "Confirm & see my valuation": POST the edited mvaInput to submitLiteValuation
 *     (the existing endpoint — this reuses the wizard's compute path, it does not
 *     duplicate it), then best-effort mark the intake session confirmed with the list of
 *     paths the owner actually touched, then hand off into the existing EmailGate flow
 *     exactly like LiteFunnel does after LiteWizard's onComplete.
 *   - A 400 from submitLiteValuation renders inline at the top naming the fields;
 *     nothing computes until it passes — confirmIntake is never called on a failed submit.
 *
 * NOTE on riskNotes: each rating shows the assistant's one-line justification
 * (services/intakeAgent.js runTurn's riskNotes), persisted server-side in
 * intake_sessions.risk_notes_json (migration 015) and fetched here via
 * getIntake(intakeId), independently of completeIntake. This fetch is a pure
 * enhancement: a getIntake failure logs to console and leaves riskNotes empty, but
 * never blocks the confirm gate from rendering or submitting. A rating with no note
 * for its key renders with no justification line (no placeholder/empty state).
 *
 * Spec: docs/superpowers/specs/2026-08-10-conversational-intake-design.md
 * Plan: .superpowers/sdd/2026-08-10-conversational-intake/task-6-brief.md
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { completeIntake, confirmIntake, getIntake, submitLiteValuation } from '../services/api';
import { RISK_QUESTIONS, INDUSTRY_OPTIONS } from '../components/lite/liteQuestions';
import RiskQuestionStep from '../components/lite/RiskQuestionStep';
import EmailGate from '../components/lite/EmailGate';
import '../components/lite/LiteWizard.css';
import './ConfirmSummary.css';

const INTAKE_ID_KEY = 'intake_id';

const PRIOR_YEARS = [1, 2, 3, 4];

/**
 * CONFIRM_RENDERED_PATHS — every mvaInput dotted path this screen renders as an
 * editable control, and therefore the exact set of paths the intake LLM is
 * allowed to write.
 *
 * SAFETY INVARIANT: every writable field must be visible and correctable on
 * this screen before anything is computed. The backend enforces the same set as
 * `WRITABLE_FIELDS` in backend/services/intakeAgent.js, and
 * backend/services/intakeAgent.drift.test.js fails if the two ever diverge.
 * ConfirmSummary.test.js separately proves this list is honest by comparing it
 * against the controls actually present in the rendered DOM — so it cannot
 * quietly claim a field is shown when it is not.
 *
 * Adding a field here without rendering it, or rendering a field without adding
 * it here, is a security defect: it lets the model move the valuation with
 * nothing on screen for the owner to catch.
 *
 * Written out as flat string literals on purpose — no spreads, no template
 * literals. The backend drift test parses this array straight out of the source
 * file, which is what makes the guard work across the frontend/backend boundary
 * (CRA cannot import from outside src/, so the list must be duplicated; this is
 * how the duplicate is kept honest). The DOM test below it proves the list
 * matches what actually renders, so the two together leave nowhere to drift.
 */
export const CONFIRM_RENDERED_PATHS = [
  // Business basics
  'company.name',
  'company.industry',
  'company.foundingYear',
  'company.employeeCount',
  'company.location',
  'financials.dealStructure',
  'ruleOfThumb.multiple',
  'ruleOfThumb.addInventory',
  // Earnings history
  'financials.lastFiscalYear',
  'financials.historicalEBITDA.yearAnchor',
  'financials.historicalEBITDA.yearMinus1',
  'financials.historicalEBITDA.yearMinus2',
  'financials.historicalEBITDA.yearMinus3',
  'financials.historicalEBITDA.yearMinus4',
  'financials.historicalSDE.yearAnchor',
  'financials.historicalSDE.yearMinus1',
  'financials.historicalSDE.yearMinus2',
  'financials.historicalSDE.yearMinus3',
  'financials.historicalSDE.yearMinus4',
  // Balance sheet
  'financials.balanceSheet.summary.totalAssets',
  'financials.balanceSheet.summary.totalLiabilities',
  'financials.balanceSheet.summary.inventory',
  // Business strength — the 26 ratings, rendered by RiskQuestionStep from
  // RISK_QUESTIONS. Listed literally here; the DOM test fails if RISK_QUESTIONS
  // ever gains, loses, or renames a key without this list following.
  'risk.liteAnswers.ownerDependency',
  'risk.liteAnswers.customerConcentration',
  'risk.liteAnswers.revenueGrowthTrend',
  'risk.liteAnswers.marginTrend',
  'risk.liteAnswers.keyPersonRisk',
  'risk.liteAnswers.processDocumentation',
  'risk.liteAnswers.competitivePosition',
  'risk.liteAnswers.recurringRevenuePercent',
  'risk.liteAnswers.managementDepth',
  'risk.liteAnswers.successionPlan',
  'risk.liteAnswers.contractQuality',
  'risk.liteAnswers.systemsTechMaturity',
  'risk.liteAnswers.regulatoryExposure',
  'risk.liteAnswers.marketShareTrend',
  'risk.liteAnswers.arAgingQuality',
  'risk.liteAnswers.cashFlowPredictability',
  'risk.liteAnswers.ipPortfolio',
  'risk.liteAnswers.litigationHistory',
  'risk.liteAnswers.boardGovernance',
  'risk.liteAnswers.industryRegulatoryEnvironment',
  'risk.liteAnswers.industryEnvironmentalExposure',
  'risk.liteAnswers.industryBusinessModelRobustness',
  'risk.liteAnswers.industryGrowthTrend',
  'risk.liteAnswers.industryMarketSentiment',
  'risk.liteAnswers.industryDemandStability',
  'risk.liteAnswers.industryLifecycleStage',
];

// ---------------------------------------------------------------------------
// Immutable dotted-path set helper over the mvaInput tree.
// ---------------------------------------------------------------------------

function setPath(obj, path, value) {
  const segments = path.split('.');
  const [head, ...rest] = segments;
  if (rest.length === 0) {
    return { ...obj, [head]: value };
  }
  return { ...obj, [head]: setPath(obj[head] || {}, rest.join('.'), value) };
}

function numberOrNull(raw) {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function displayValue(value) {
  return value === null || value === undefined ? '' : String(value);
}

function ConfirmSummary() {
  const location = useLocation();

  const [intakeId] = useState(() => {
    const fromState = location.state && location.state.intakeId;
    if (fromState) return fromState;
    return window.sessionStorage.getItem(INTAKE_ID_KEY) || null;
  });

  const [loadState, setLoadState] = useState(intakeId ? 'loading' : 'missing'); // loading|missing|error|ready
  const [mvaInput, setMvaInput] = useState(null);
  const [editedPaths, setEditedPaths] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [valuationId, setValuationId] = useState(null);
  const [riskNotes, setRiskNotes] = useState({});

  useEffect(() => {
    if (!intakeId) return;
    let cancelled = false;
    completeIntake(intakeId)
      .then((data) => {
        if (cancelled) return;
        setMvaInput(data.mvaInput);
        setLoadState('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Could not load this conversation for review:', err);
        setLoadState('error');
      });

    // riskNotes (per-rating justification) is an enhancement, never a blocker: fetched
    // independently of completeIntake so a getIntake failure can never stop the confirm
    // gate from rendering or submitting — it just leaves justifications blank.
    getIntake(intakeId)
      .then((data) => {
        if (cancelled) return;
        setRiskNotes((data && data.riskNotes) || {});
      })
      .catch((err) => {
        console.error('Could not load risk-rating justifications (non-blocking):', err);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldChange = (path, rawValue, { numeric = false } = {}) => {
    const value = numeric ? numberOrNull(rawValue) : rawValue;
    setMvaInput((prev) => setPath(prev, path, value));
    setEditedPaths((prev) => {
      const next = new Set(prev);
      next.add(path);
      return next;
    });
  };

  const handleCheckboxYesNo = (path, checked) => {
    handleFieldChange(path, checked ? 'YES' : 'NO');
  };

  const handleRiskAnswer = (key, value) => {
    handleFieldChange(`risk.liteAnswers.${key}`, value);
  };

  const handleConfirm = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const result = await submitLiteValuation(mvaInput);
      try {
        await confirmIntake(intakeId, Array.from(editedPaths));
      } catch (err) {
        // Best-effort bookkeeping only — the valuation already computed successfully,
        // so a failure here must not strand the owner mid-flow.
        console.error('Could not mark intake session confirmed:', err);
      }
      // Clear the intake id now that the session is confirmed: leaving it behind
      // would let a later visit to /valuation/chat resume this (now-confirmed)
      // session and flip its status back to active/ready on the next turn, and
      // would 409 a refresh of this very page into the "start over" error state.
      try {
        window.sessionStorage.removeItem(INTAKE_ID_KEY);
      } catch (err) {
        // sessionStorage can throw (private browsing, disabled). Best-effort
        // only — never block the success hand-off on it.
        console.error('Could not clear intake session id:', err);
      }
      setValuationId(result.valuationId);
    } catch (err) {
      setSubmitting(false);
      if (err.response && err.response.status === 400) {
        const data = err.response.data || {};
        const msgs = data.errors ? data.errors.join(' ') : (data.error || 'Invalid input.');
        setSubmitError(msgs);
      } else {
        setSubmitError('Network error — please check your connection and try again.');
      }
    }
  };

  if (valuationId) {
    return <EmailGate valuationId={valuationId} />;
  }

  if (loadState === 'missing') {
    return (
      <div className="confirm-summary-page">
        <div className="confirm-summary-startover">
          <h2>We lost track of your conversation</h2>
          <p>Let&apos;s start over — nothing you entered was saved without a session to attach it to.</p>
          <Link to="/valuation" className="lite-wizard-btn lite-wizard-btn--primary">
            Start over
          </Link>
        </div>
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <div className="confirm-summary-page">
        <div className="confirm-summary-startover">
          <h2>We couldn&apos;t load your conversation</h2>
          <p>Something went wrong pulling together your answers. Please start over.</p>
          <Link to="/valuation" className="lite-wizard-btn lite-wizard-btn--primary">
            Start over
          </Link>
        </div>
      </div>
    );
  }

  if (loadState === 'loading' || !mvaInput) {
    return (
      <div className="confirm-summary-page">
        <p className="confirm-summary-loading" role="status">Loading your answers&hellip;</p>
      </div>
    );
  }

  const company = mvaInput.company;
  const financials = mvaInput.financials;
  const balanceSheet = financials.balanceSheet.summary;
  const ruleOfThumb = mvaInput.ruleOfThumb;
  const liteAnswers = mvaInput.risk.liteAnswers;

  return (
    <div className="confirm-summary-page">
      <div className="confirm-summary">
        <h1 className="confirm-summary-title">Review your answers</h1>
        <p className="confirm-summary-sub">
          Here&apos;s everything we gathered. Fix anything that looks off before we calculate your valuation.
        </p>

        {submitError && (
          <div className="lite-wizard-error-banner" role="alert">
            <p>{submitError}</p>
          </div>
        )}

        {/* ---- Business basics ---- */}
        <section className="confirm-summary-group">
          <h2 className="confirm-summary-group-title">Business basics</h2>

          <div className="lite-wizard-field">
            <label htmlFor="cs-company-name">Company name</label>
            <input
              type="text"
              id="cs-company-name"
              data-path="company.name"
              value={displayValue(company.name)}
              onChange={(e) => handleFieldChange('company.name', e.target.value)}
            />
          </div>

          <div className="lite-wizard-field">
            <label htmlFor="cs-industry">Industry</label>
            <select
              id="cs-industry"
              data-path="company.industry"
              value={displayValue(company.industry)}
              onChange={(e) => handleFieldChange('company.industry', e.target.value)}
            >
              <option value="">— choose industry —</option>
              {INDUSTRY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="lite-wizard-field">
            <label htmlFor="cs-foundingYear">Year founded</label>
            <input
              type="number"
              id="cs-foundingYear"
              data-path="company.foundingYear"
              value={displayValue(company.foundingYear)}
              onChange={(e) => handleFieldChange('company.foundingYear', e.target.value, { numeric: true })}
            />
          </div>

          <div className="lite-wizard-field">
            <label htmlFor="cs-employeeCount">Number of employees</label>
            <input
              type="number"
              id="cs-employeeCount"
              data-path="company.employeeCount"
              value={displayValue(company.employeeCount)}
              onChange={(e) => handleFieldChange('company.employeeCount', e.target.value, { numeric: true })}
            />
          </div>

          <div className="lite-wizard-field">
            <label htmlFor="cs-location">Location</label>
            <input
              type="text"
              id="cs-location"
              data-path="company.location"
              value={displayValue(company.location)}
              onChange={(e) => handleFieldChange('company.location', e.target.value)}
            />
          </div>

          <div className="lite-wizard-field lite-wizard-field--checkbox">
            <input
              type="checkbox"
              id="cs-canadianCompany"
              data-path="financials.dealStructure"
              checked={financials.dealStructure === 'YES'}
              onChange={(e) => handleCheckboxYesNo('financials.dealStructure', e.target.checked)}
            />
            <label htmlFor="cs-canadianCompany">Is this a Canadian company?</label>
          </div>

          <div className="lite-wizard-field">
            <label htmlFor="cs-ruleOfThumbMultiple">Rule-of-thumb multiple</label>
            <input
              type="number"
              id="cs-ruleOfThumbMultiple"
              data-path="ruleOfThumb.multiple"
              step="0.1"
              value={displayValue(ruleOfThumb.multiple)}
              onChange={(e) => handleFieldChange('ruleOfThumb.multiple', e.target.value, { numeric: true })}
            />
          </div>

          <div className="lite-wizard-field lite-wizard-field--checkbox">
            <input
              type="checkbox"
              id="cs-addInventory"
              data-path="ruleOfThumb.addInventory"
              checked={ruleOfThumb.addInventory === 'YES'}
              onChange={(e) => handleCheckboxYesNo('ruleOfThumb.addInventory', e.target.checked)}
            />
            <label htmlFor="cs-addInventory">Add inventory to valuation</label>
          </div>
        </section>

        {/* ---- Earnings history ---- */}
        <section className="confirm-summary-group">
          <h2 className="confirm-summary-group-title">Earnings history</h2>

          <div className="lite-wizard-field">
            <label htmlFor="cs-lastFiscalYear">Last completed fiscal year</label>
            <input
              type="number"
              id="cs-lastFiscalYear"
              data-path="financials.lastFiscalYear"
              value={displayValue(financials.lastFiscalYear)}
              onChange={(e) => handleFieldChange('financials.lastFiscalYear', e.target.value, { numeric: true })}
            />
          </div>

          <div className="lite-wizard-field">
            <label htmlFor="cs-ebitdaAnchor">EBITDA (most recent year)</label>
            <input
              type="number"
              id="cs-ebitdaAnchor"
              data-path="financials.historicalEBITDA.yearAnchor"
              value={displayValue(financials.historicalEBITDA.yearAnchor)}
              onChange={(e) => handleFieldChange('financials.historicalEBITDA.yearAnchor', e.target.value, { numeric: true })}
            />
          </div>

          <div className="lite-wizard-field">
            <label htmlFor="cs-sdeAnchor">SDE (most recent year)</label>
            <input
              type="number"
              id="cs-sdeAnchor"
              data-path="financials.historicalSDE.yearAnchor"
              value={displayValue(financials.historicalSDE.yearAnchor)}
              onChange={(e) => handleFieldChange('financials.historicalSDE.yearAnchor', e.target.value, { numeric: true })}
            />
          </div>

          {PRIOR_YEARS.map((n) => (
            <div key={n} className="confirm-summary-prior-row">
              <div className="lite-wizard-field">
                <label htmlFor={`cs-ebitdaMinus${n}`}>EBITDA — {n} year{n > 1 ? 's' : ''} earlier</label>
                <input
                  type="number"
                  id={`cs-ebitdaMinus${n}`}
                  data-path={`financials.historicalEBITDA.yearMinus${n}`}
                  value={displayValue(financials.historicalEBITDA[`yearMinus${n}`])}
                  onChange={(e) => handleFieldChange(`financials.historicalEBITDA.yearMinus${n}`, e.target.value, { numeric: true })}
                />
              </div>
              <div className="lite-wizard-field">
                <label htmlFor={`cs-sdeMinus${n}`}>SDE — {n} year{n > 1 ? 's' : ''} earlier</label>
                <input
                  type="number"
                  id={`cs-sdeMinus${n}`}
                  data-path={`financials.historicalSDE.yearMinus${n}`}
                  value={displayValue(financials.historicalSDE[`yearMinus${n}`])}
                  onChange={(e) => handleFieldChange(`financials.historicalSDE.yearMinus${n}`, e.target.value, { numeric: true })}
                />
              </div>
            </div>
          ))}
        </section>

        {/* ---- Balance sheet ---- */}
        <section className="confirm-summary-group">
          <h2 className="confirm-summary-group-title">Balance sheet</h2>

          <div className="lite-wizard-field">
            <label htmlFor="cs-totalAssets">Total assets ($)</label>
            <input
              type="number"
              id="cs-totalAssets"
              data-path="financials.balanceSheet.summary.totalAssets"
              value={displayValue(balanceSheet.totalAssets)}
              onChange={(e) => handleFieldChange('financials.balanceSheet.summary.totalAssets', e.target.value, { numeric: true })}
            />
          </div>

          <div className="lite-wizard-field">
            <label htmlFor="cs-totalLiabilities">Total liabilities ($)</label>
            <input
              type="number"
              id="cs-totalLiabilities"
              data-path="financials.balanceSheet.summary.totalLiabilities"
              value={displayValue(balanceSheet.totalLiabilities)}
              onChange={(e) => handleFieldChange('financials.balanceSheet.summary.totalLiabilities', e.target.value, { numeric: true })}
            />
          </div>

          <div className="lite-wizard-field">
            <label htmlFor="cs-inventory">Inventory ($, optional)</label>
            <input
              type="number"
              id="cs-inventory"
              data-path="financials.balanceSheet.summary.inventory"
              value={displayValue(balanceSheet.inventory)}
              onChange={(e) => handleFieldChange('financials.balanceSheet.summary.inventory', e.target.value, { numeric: true })}
            />
          </div>
        </section>

        {/* ---- Business strength (26 ratings) ---- */}
        <section className="confirm-summary-group">
          <h2 className="confirm-summary-group-title">Business strength</h2>
          <p className="lite-wizard-step-sub">
            For each question, pick the description that best matches your business.
          </p>
          <RiskQuestionStep
            questions={RISK_QUESTIONS}
            answers={liteAnswers}
            errors={{}}
            onAnswer={handleRiskAnswer}
            notes={riskNotes}
          />
        </section>

        <div className="confirm-summary-actions">
          <button
            type="button"
            className="lite-wizard-btn lite-wizard-btn--primary"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? 'Calculating…' : 'Confirm & see my valuation'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmSummary;
